import { animate, group, query, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, HostListener, Injectable, OnInit, ViewChild } from '@angular/core';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AnimationOptions } from 'ngx-lottie';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { AppSettings } from 'src/app/app-settings';
import { InfoCardComponent } from 'src/app/components/shared/info-card/info-card.component';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { ReportsService } from 'src/app/services/reports.service';
import {
  countdownLabel,
  daysUntil,
  eventDateLong,
  eventDateShort,
  isOngoing,
  isPast,
  parseWallClock,
  purchaseDate
} from 'src/app/utils/profile-dates.util';
import { CalendarEvent, calendarPlatform, iosSinSafari, icsUrl, openGoogleCalendar, outlookCalendarUrl } from 'src/app/utils/calendar-links.util';

/**
 * Una fila del listado es UN EVENTO DENTRO DE UN PEDIDO, no un pedido.
 *
 * Un pedido puede mezclar varios eventos (y hasta uno futuro con otro ya
 * celebrado), así que agruparlo por pedido obligaba al usuario a abrirlo para
 * saber de qué era. Los datos del pedido —número, fecha de compra, estado—
 * siguen estando, en la propia fila y en el detalle.
 */
interface TicketRow {
  /** Clave interna para agrupar y para el trackBy. No sale a la URL. */
  key: string;
  orderId: number;
  eventId: number | null;
  sessionId: number | null;
  orderNumber: string;
  createdAt: string;
  purchasedOn: string;
  isSeasonPassOrder: boolean;

  eventTitle: string;
  eventImage: string | null;
  spaceName: string | null;
  address: string | null;

  /** Hora de pared de Madrid, tal cual viene de la API. */
  startDate: string | null;
  dateShort: string;
  dateLong: string;
  countdown: string;
  /** Es hoy: el banner cambia su etiqueta por la pill verde de "¡Es hoy!". */
  today: boolean;
  past: boolean;

  total: number;
  quantity: number;

  /** Todas las entradas de este evento en este pedido. */
  tickets: any[];

  /** Devuelta entera: ni se abre, ni se descarga, ni tiene QR. */
  refunded: boolean;
  partiallyRefunded: boolean;
  /** Cuántas de las entradas de la fila están devueltas. */
  refundedCount: number;
  /**
   * Cuántos eventos (evento y sesión) trae el pedido entero. El PDF de
   * descarga es del pedido, así que si es más de uno el banner lo avisa.
   */
  eventsInOrder: number;
}

/**
 * El adaptador de fechas nativo de Material, corregido para España en las
 * dos cosas en que ignora el locale:
 *
 *  - Empieza la semana en domingo: `getFirstDayOfWeek` devuelve 0 fijo. Un
 *    calendario con la D delante hace que se pinche el día de al lado.
 *
 *  - Lee lo que se teclea en formato AMERICANO: su `parse` es un
 *    `Date.parse`, así que "1/9/2026" era el 9 de enero y no el 1 de
 *    septiembre. Con el calendario no pasa, pero quien escribía la fecha a
 *    mano filtraba por otro día sin enterarse. Aquí se lee día/mes/año.
 *
 * Va solo en este componente y no en toda la app para no cambiarle nada a
 * otros calendarios que pueda haber.
 */
@Injectable()
class EsDateAdapter extends NativeDateAdapter {
  override getFirstDayOfWeek(): number {
    return 1;
  }

  override parse(value: any, parseFormat?: any): Date | null {
    if (typeof value !== 'string') {
      return super.parse(value, parseFormat);
    }

    const texto = value.trim();
    if (texto === '') {
      return null;
    }

    // 1/9/2026, 01-09-2026, 1.9.26…
    const partes = texto.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2}|\d{4})$/);
    if (!partes) {
      return this.invalid();
    }

    const dia = Number(partes[1]);
    const mes = Number(partes[2]) - 1;
    const anio = partes[3].length === 2 ? 2000 + Number(partes[3]) : Number(partes[3]);
    const fecha = new Date(anio, mes, dia);

    // Un 31/02 no se convierte en un 3 de marzo: se da por no válido.
    if (fecha.getFullYear() !== anio || fecha.getMonth() !== mes || fecha.getDate() !== dia) {
      return this.invalid();
    }

    return fecha;
  }
}

/**
 * El texto del plan que se va sale hacia un lado y el del que llega entra
 * desde el otro, uno detrás del otro: si se cruzan, a mitad se leen los dos
 * títulos montados. La foto solo funde, que desplazada enseñaba el borde de
 * su celda. Desplazamiento corto, para no tener que recortar el banner, que
 * no puede llevar `overflow: hidden` por el menú del calendario.
 */
function cambioDePlan(sentido: 1 | -1) {
  return group([
    query('.next-plan-body:leave', [
      animate('{{sale}}ms cubic-bezier(0.4, 0, 1, 1)', style({ opacity: 0, transform: `translateX(${-40 * sentido}px)` }))
    ], { optional: true }),
    query('.next-plan-body:enter', [
      style({ opacity: 0, transform: `translateX(${40 * sentido}px)` }),
      animate('{{entra}}ms {{sale}}ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 1, transform: 'none' }))
    ], { optional: true }),
    query('.next-plan-image:leave', [
      animate('{{sale}}ms {{sale}}ms linear', style({ opacity: 0 }))
    ], { optional: true }),
    query('.next-plan-image:enter', [
      style({ opacity: 0 }),
      animate('{{entra}}ms 80ms ease-out', style({ opacity: 1 }))
    ], { optional: true })
  ]);
}

@Component({
  selector: 'app-tickets-profile',
  templateUrl: './tickets-profile.component.html',
  styleUrls: ['./tickets-profile.component.scss'],
  providers: [{ provide: DateAdapter, useClass: EsDateAdapter }],
  animations: [
    // El cambio de plan del banner. Va atado a un contador de pasos y no al
    // índice: al dar la vuelta del último al primero el índice baja, y la
    // tarjeta tiene que seguir saliendo por la izquierda. El que se va y el
    // que llega comparten celda de la rejilla (ver el scss), así que se
    // cruzan en el sitio sin que el banner cambie de alto a saltos.
    trigger('cambioPlan', [
      transition(':increment', cambioDePlan(1)),
      transition(':decrement', cambioDePlan(-1))
    ])
  ]
})
export class TicketsProfileComponent implements OnInit {

  urlEvents: string = AppSettings.MEDIA_URI + '/events_images/';

  rows: TicketRow[] = [];
  counts: any = { upcoming: 0, past: 0 };

  scope: 'upcoming' | 'past' = 'upcoming';
  search: string = '';

  showFilters: boolean = false;
  filters: any = this.emptyFilters();
  /** Copia de trabajo: el panel no filtra hasta que le das a Aplicar. */
  draftFilters: any = this.emptyFilters();

  nextPlan: TicketRow | null = null;
  /**
   * Los planes que pasa el banner: todos los de hoy cuando hay más de uno y,
   * si no, solo el próximo. `nextPlan` es siempre el que se ve.
   */
  nextPlans: TicketRow[] = [];
  nextPlanIndex: number = 0;
  /** Sube al avanzar y baja al retroceder; es lo que mira la animación. */
  pasosPlan: number = 0;
  /** Lo que dura cada plan en pantalla antes de pasar al siguiente. */
  readonly segundosPorPlan = 7;
  /** Ratón encima, foco dentro o pestaña oculta: el carrusel se espera. */
  private planEnPausa = { raton: false, foco: false, oculta: false };
  private toqueInicioX: number | null = null;
  private toqueInicioY = 0;
  /** Menú de "Añadir al calendario" del banner. */
  showCalendar: boolean = false;

  page: number = 1;
  items: number = 12;
  loadingPage: boolean = false;
  finishScroll: boolean = false;

  private searchChanged = new Subject<string>();

  @ViewChild('containerEvents', { static: false }) containerEvents: ElementRef;
  loadingLottie: AnimationOptions = {
    path: 'assets/lotties/loading.json',
  };

  constructor(
    private auth: AuthenticationService,
    private dialog: MatDialog,
    private router: Router,
    private reportService: ReportsService
  ) { }

  ngOnInit(): void {
    // Se espera a que el usuario deje de teclear: si no, cada letra es una
    // consulta y las respuestas pueden llegar desordenadas.
    this.searchChanged
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe(() => this.initLoading());

    this.initLoading();
    this.getNextPlan();
  }

  // ===== Carga =====

  initLoading() {
    this.rows = [];
    this.page = 1;
    this.finishScroll = false;
    this.loadingPage = true;
    this.getData();
  }

  getData() {
    const params = {
      scope: this.scope,
      search: this.search,
      event_from: this.diaDe(this.filters.eventFrom),
      event_to: this.diaDe(this.filters.eventTo),
      bought_from: this.diaDe(this.filters.boughtFrom),
      bought_to: this.diaDe(this.filters.boughtTo),
      refund: this.filters.refund
    };

    this.reportService.getOrdersByUser(this.page, this.items, params).subscribe({
      next: (res) => {
        const orders = res?.data?.data || [];
        this.counts = res?.data?.counts || this.counts;

        const nuevas = Array.isArray(orders) ? this.buildRows(orders) : [];
        this.rows = this.rows.concat(nuevas);

        // El corte se mira sobre los pedidos que trae la página, no sobre las
        // filas: un pedido con tres eventos da tres filas y nos haría creer
        // que queda más por cargar.
        this.finishScroll = orders.length < this.items;
        this.loadingPage = false;
      },
      error: () => {
        this.loadingPage = false;
        this.finishScroll = true;
      }
    });
  }

  /**
   * El banner "Tu próximo plan". Va por su lado y sin filtros: aunque el
   * usuario esté mirando las pasadas, lo que tiene por delante sigue siendo
   * lo mismo.
   */
  getNextPlan() {
    this.reportService.getNextEventByUser().subscribe({
      next: (res) => {
        const order = res?.data?.order;
        if (!order) {
          this.nextPlan = null;
          return;
        }

        // El pedido del próximo plan puede venir también entre los de hoy, y
        // un pedido trae todos sus eventos: se quitan las filas repetidas.
        const pedidos = [order, ...(res?.data?.today || [])];
        const vistas = new Set<string>();
        const filas = this.buildRows(pedidos, 'upcoming')
          .filter(row => !row.past && !row.refunded)
          .filter(row => !vistas.has(row.key) && !!vistas.add(row.key));

        const deHoy = filas.filter(row => row.today);
        this.nextPlans = deHoy.length > 1 ? deHoy : filas.slice(0, 1);
        this.nextPlanIndex = 0;
        this.nextPlan = this.nextPlans[0] ?? null;
      },
      error: () => {
        this.nextPlans = [];
        this.nextPlan = null;
      }
    });
  }

  // ===== Construcción de las filas =====

  /**
   * De pedidos a filas: se parten las líneas por evento y sesión, y cada
   * grupo es una fila con su recuento y su importe.
   */
  private buildRows(orders: any[], scope: 'upcoming' | 'past' = this.scope): TicketRow[] {
    const filas: TicketRow[] = [];

    orders.forEach(order => {
      const cartLines = order.cart?.cart_lines || [];

      // Reservas que genera el alta de abonados al publicar el evento
      // (SeasonPassEventBookingService): el pedido no es una compra, no tiene
      // número ni descarga en el TPV.
      const isSeasonPassOrder = cartLines.length > 0 && cartLines.every((line: any) =>
        (line.bookings || []).length > 0 && line.bookings.every((booking: any) => !!booking.id_season_pass));

      const grupos: { [key: string]: TicketRow } = {};

      cartLines.forEach((line: any) => {
        const event = line.event || {};
        const startDate = line.session?.start_date || event.start_date || null;
        const clave = `${order.id}-${event.id}-${line.session?.id || 0}`;

        if (!grupos[clave]) {
          grupos[clave] = this.emptyRow(order, event, line.session, startDate, clave, isSeasonPassOrder);
        }

        const fila = grupos[clave];

        (line.bookings || []).forEach((booking: any) => {
          fila.tickets.push(this.buildTicket(order, line, booking));
          fila.quantity++;
          fila.total += Number(booking.price ?? line.price ?? 0);
        });
      });

      const eventosDelPedido = Object.keys(grupos).length;

      Object.values(grupos).forEach(fila => {
        fila.eventsInOrder = eventosDelPedido;
        fila.refundedCount = fila.tickets.filter(t => t.refund).length;
        fila.refunded = fila.tickets.length > 0 && fila.refundedCount === fila.tickets.length;
        fila.partiallyRefunded = !fila.refunded && fila.refundedCount > 0;
        filas.push(fila);
      });
    });

    // Un pedido que mezcla un evento de mañana con otro de la semana pasada
    // viene en las dos pestañas, así que aquí se queda solo lo que toca.
    return filas
      .filter(fila => (scope === 'past') === fila.past)
      .sort((a, b) => this.orderByDate(a, b));
  }

  private emptyRow(order: any, event: any, session: any, startDate: string | null, key: string, isSeasonPassOrder: boolean): TicketRow {
    return {
      key,
      orderId: order.id,
      eventId: event?.id ?? null,
      sessionId: session?.id ?? null,
      orderNumber: order.invoice_number,
      createdAt: order.created_at,
      purchasedOn: purchaseDate(order.created_at),
      isSeasonPassOrder,

      eventTitle: event.title || '',
      eventImage: event.image_url || null,
      spaceName: event.space?.name || null,
      address: event.space?.address || event.address || null,

      startDate,
      dateShort: eventDateShort(startDate),
      dateLong: eventDateLong(startDate),
      countdown: countdownLabel(startDate),
      // Uno que empezó anoche a las 23:00 y sigue a la 01:00 también es "hoy"
      // para el banner: es el plan que está pasando.
      today: daysUntil(startDate) === 0 || isOngoing(startDate),
      past: isPast(startDate),

      total: 0,
      quantity: 0,
      tickets: [],
      refunded: false,
      partiallyRefunded: false,
      refundedCount: 0,
      eventsInOrder: 1
    };
  }

  private buildTicket(order: any, line: any, booking: any) {
    const seat = booking?.seat;
    // Una reserva de abonado no tiene ticket ni hash propio: el nombre sale
    // del abono y el QR de la puerta es el del propio abono. El abono puede
    // no venir (una baja lo borra en soft delete), así que quien manda para
    // reconocerla es la propia clave.
    const isSeasonPass = !!booking?.id_season_pass;
    const seasonPass = booking?.season_pass || null;

    return {
      id: booking.id,
      event: line.event?.title || null,
      event_image: line.event?.image_url || null,
      ticket_name: isSeasonPass ? 'Abono de temporada' : (line.ticket?.name || ''),
      isSeasonPass,
      seasonPassNumber: seasonPass?.member_number || null,
      priceTotal: booking.price ?? line.price ?? 0,
      date: line.session?.start_date || line.event?.start_date || null,
      sectionName: seat?.map_section?.name || null,
      seat: seat?.seat || null,
      row: seat?.row || null,
      // Qué parte de la posición enseña esta sección. Antes se derivaba de
      // `seat.hidden`, que es otra cosa (y está siempre a false), así que la
      // fila salía siempre. Ahora manda la configuración de la sección.
      show_row: (seat?.map_section?.show_row ?? true) !== false,
      show_seat_label: (seat?.map_section?.show_seat_label ?? true) !== false,
      localizador: booking?.hash || seasonPass?.hash || '',
      status: booking.status || null,
      refund: booking.status?.name == 'refunded',
      // El titular se congela en el pedido, no en cada entrada: es el
      // comprador y es el mismo para todas. Hace falta además el flag del
      // evento porque un pedido puede mezclar un evento nominativo con otro
      // que no lo es.
      nominative: !!line.event?.nominative_tickets && !!order.holder_name,
      holderName: order.holder_name || null
    };
  }

  /** Lo que antes llega primero en próximas; lo último celebrado en pasadas. */
  private orderByDate(a: TicketRow, b: TicketRow): number {
    const ta = parseWallClock(a.startDate)?.date.getTime() ?? 0;
    const tb = parseWallClock(b.startDate)?.date.getTime() ?? 0;
    return this.scope === 'past' ? tb - ta : ta - tb;
  }

  // ===== Pestañas, buscador y filtros =====

  setScope(scope: 'upcoming' | 'past') {
    if (this.scope === scope) {
      return;
    }
    this.scope = scope;
    this.initLoading();
  }

  onSearch(value: string) {
    this.search = value;
    this.searchChanged.next(value);
  }

  clearSearch() {
    this.search = '';
    this.initLoading();
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
    if (this.showFilters) {
      this.draftFilters = { ...this.filters };
    }
  }

  applyFilters() {
    // Una fecha tecleada que no existe (31/02, "mañana") llega como fecha no
    // válida: se descarta aquí, para que no salga una chip vacía ni cuente
    // como filtro. La API ya la ignoraba, pero el usuario no lo sabía.
    const valida = (d: any) => (d instanceof Date && !isNaN(d.getTime()) ? d : null);
    this.draftFilters = {
      ...this.draftFilters,
      eventFrom: valida(this.draftFilters.eventFrom),
      eventTo: valida(this.draftFilters.eventTo),
      boughtFrom: valida(this.draftFilters.boughtFrom),
      boughtTo: valida(this.draftFilters.boughtTo)
    };
    this.filters = { ...this.draftFilters };
    this.showFilters = false;
    this.initLoading();
  }

  clearFilters() {
    this.draftFilters = this.emptyFilters();
    this.filters = this.emptyFilters();
    this.showFilters = false;
    this.initLoading();
  }

  /**
   * Cuántos filtros hay puestos. Un rango de fechas cuenta como uno, que es
   * como se ve ahora en el panel: un solo campo por rango.
   */
  get activeFiltersCount(): number {
    const f = this.filters;
    let total = 0;
    if (f.eventFrom || f.eventTo) total++;
    if (f.boughtFrom || f.boughtTo) total++;
    if (f.refund !== 'all') total++;
    return total;
  }

  /**
   * Las chips de los filtros aplicados, una por filtro: un rango de fechas es
   * una sola, igual que en el contador. Salen de `filters` y no del borrador
   * del panel, que lo que enseñan es lo que está filtrando la lista ahora.
   */
  get filterChips(): { key: 'event' | 'bought' | 'refund'; label: string }[] {
    const f = this.filters;
    const chips: { key: 'event' | 'bought' | 'refund'; label: string }[] = [];

    const rango = (quien: string, desde: Date | null, hasta: Date | null) => {
      if (desde && hasta) return `${quien} del ${this.diaLegible(desde)} al ${this.diaLegible(hasta)}`;
      if (desde) return `${quien} desde el ${this.diaLegible(desde)}`;
      return `${quien} hasta el ${this.diaLegible(hasta)}`;
    };

    if (f.eventFrom || f.eventTo) {
      chips.push({ key: 'event', label: rango('Eventos', f.eventFrom, f.eventTo) });
    }

    if (f.boughtFrom || f.boughtTo) {
      chips.push({ key: 'bought', label: rango('Compradas', f.boughtFrom, f.boughtTo) });
    }

    if (f.refund === 'without') {
      chips.push({ key: 'refund', label: 'Sin devolución' });
    } else if (f.refund === 'only') {
      chips.push({ key: 'refund', label: 'Devueltas' });
    }

    return chips;
  }

  /** Quita un solo filtro y recarga, sin tocar los demás. */
  removeFilter(key: 'event' | 'bought' | 'refund') {
    const f = { ...this.filters };

    if (key === 'event') {
      f.eventFrom = null;
      f.eventTo = null;
    } else if (key === 'bought') {
      f.boughtFrom = null;
      f.boughtTo = null;
    } else {
      f.refund = 'all';
    }

    this.filters = f;
    // Si el panel está abierto, que no le vuelva a meter el filtro al Aplicar.
    this.draftFilters = { ...f };
    this.initLoading();
  }

  /** 01/09/2026: con ceros, como se lee una fecha en una etiqueta. */
  private diaLegible(fecha: Date | null): string {
    if (!(fecha instanceof Date) || isNaN(fecha.getTime())) {
      return '';
    }

    const dos = (n: number) => String(n).padStart(2, '0');
    return `${dos(fecha.getDate())}/${dos(fecha.getMonth() + 1)}/${fecha.getFullYear()}`;
  }

  /**
   * Las fechas del panel son `Date` (las da el calendario de rango) y la API
   * quiere `YYYY-MM-DD`. Se montan a mano en hora local: el calendario crea
   * la medianoche local, y `toISOString()` la pasaría a UTC, que en España es
   * el día anterior a las 22:00 o las 23:00. La API ya se encarga de abrir y
   * cerrar ese día en Madrid.
   */
  private diaDe(fecha: Date | null): string {
    if (!(fecha instanceof Date) || isNaN(fecha.getTime())) {
      return '';
    }

    const dos = (n: number) => String(n).padStart(2, '0');
    return `${fecha.getFullYear()}-${dos(fecha.getMonth() + 1)}-${dos(fecha.getDate())}`;
  }

  private emptyFilters() {
    return {
      eventFrom: null as Date | null,
      eventTo: null as Date | null,
      boughtFrom: null as Date | null,
      boughtTo: null as Date | null,
      refund: 'all'
    };
  }

  // ===== Acciones =====

  /**
   * El pedido va en la ruta y el evento en la query, porque un pedido puede
   * traer varios y hay que decirle al detalle cuál se abre. La sesión solo se
   * pasa si la hay: los eventos de un solo día no tienen.
   */
  openTickets(row: TicketRow) {
    if (row.refunded) {
      return;
    }

    const queryParams: any = { event: row.eventId };
    if (row.sessionId) {
      queryParams.session = row.sessionId;
    }

    this.router.navigate(['/profile/entradas', row.orderId], { queryParams });
  }

  /** Abre o cierra el menú de "Añadir al calendario". */
  // ===== Carrusel del banner (varios planes hoy) =====

  /**
   * Al plan `indice`. El sentido lo da la flecha implícita: hacia delante si
   * el destino va después, salvo la vuelta automática del último al primero,
   * que también es "hacia delante".
   */
  irAPlan(indice: number, haciaDelante = indice > this.nextPlanIndex) {
    if (indice === this.nextPlanIndex || !this.nextPlans[indice]) {
      return;
    }

    this.pasosPlan += haciaDelante ? 1 : -1;
    this.nextPlanIndex = indice;
    this.nextPlan = this.nextPlans[indice];
    this.showCalendar = false;
  }

  /**
   * Lo llama el `animationend` de la barrita del punto activo. Que el ritmo lo
   * lleve la propia barra hace que pausar sea solo parar su animación, y que
   * al seguir no se salte ni se reinicie la cuenta.
   */
  siguientePlan() {
    if (this.nextPlans.length > 1) {
      this.irAPlan((this.nextPlanIndex + 1) % this.nextPlans.length, true);
    }
  }

  anteriorPlan() {
    const total = this.nextPlans.length;
    if (total > 1) {
      this.irAPlan((this.nextPlanIndex - 1 + total) % total, false);
    }
  }

  get carruselEnPausa(): boolean {
    const p = this.planEnPausa;
    return p.raton || p.foco || p.oculta || this.showCalendar;
  }

  /** Solo con ratón: en el móvil un toque deja el hover pegado y lo pararía. */
  pausaPorRaton(event: PointerEvent, pausa: boolean) {
    if (event.pointerType === 'mouse') {
      this.planEnPausa.raton = pausa;
    }
  }

  pausaPorFoco(pausa: boolean) {
    this.planEnPausa.foco = pausa;
  }

  @HostListener('document:visibilitychange')
  pausaPorPestana() {
    this.planEnPausa.oculta = document.hidden;
  }

  /** En el móvil, deslizar el banner también cambia de plan. */
  empiezaDeslizarPlan(event: PointerEvent) {
    if (this.nextPlans.length > 1 && event.pointerType !== 'mouse') {
      this.toqueInicioX = event.clientX;
      this.toqueInicioY = event.clientY;
    }
  }

  sueltaDeslizarPlan(event: PointerEvent) {
    if (this.toqueInicioX === null) {
      return;
    }

    const dx = event.clientX - this.toqueInicioX;
    const dy = event.clientY - this.toqueInicioY;
    this.toqueInicioX = null;

    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      dx < 0 ? this.siguientePlan() : this.anteriorPlan();
    }
  }

  /** Las entradas del plan que siguen valiendo: las devueltas no cuentan. */
  entradasVigentes(plan: TicketRow): number {
    return plan.quantity - plan.refundedCount;
  }

  /** Sin animación para quien la tiene quitada en el sistema. */
  get animacionPlan() {
    const quieto = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    return { value: this.pasosPlan, params: { sale: quieto ? 0 : 220, entra: quieto ? 0 : 380 } };
  }

  toggleCalendar() {
    this.showCalendar = !this.showCalendar;
  }

  /** Un clic fuera lo cierra, que si no se queda abierto para siempre. */
  @HostListener('document:click', ['$event'])
  cerrarCalendario(event: any) {
    if (this.showCalendar && !event.target.closest('.calendar-menu')) {
      this.showCalendar = false;
    }

    // El panel de filtros se cierra igual, sin aplicar: lo que hubiera a
    // medias se descarta, como con la X. Los clics dentro del calendario de
    // rango no cuentan como "fuera": el calendario se pinta en una capa aparte
    // (`cdk-overlay-container`), fuera del panel.
    //
    // Y al pinchar el último día del rango, Material cierra el calendario y
    // quita su capa del DOM antes de que llegue aquí el clic: el elemento ya
    // no está en la página y `closest` no encuentra nada. Sin esta guarda,
    // elegir una fecha cerraba el panel entero.
    if (!document.contains(event.target)) {
      return;
    }

    if (this.showFilters
      && !event.target.closest('.filters-anchor')
      && !event.target.closest('.cdk-overlay-container')) {
      this.showFilters = false;
    }
  }

  private eventoDeCalendario(row: TicketRow): CalendarEvent {
    return {
      title: row.eventTitle,
      startDate: row.startDate,
      location: [row.spaceName, row.address].filter(Boolean).join(', ') || null,
      eventId: row.eventId,
      sessionId: row.sessionId
    };
  }

  /** Mismo reparto que en la pantalla de la entrada (ver TicketDetailComponent). */
  readonly plataforma = calendarPlatform();
  /** iPhone sin Safari: el .ics no llega al Calendario (ver `iosSinSafari`). */
  readonly sinSafari = iosSinSafari();

  get nativeCalendarLabel(): string {
    if (this.plataforma === 'ios') {
      return 'Calendario del iPhone';
    }
    return this.plataforma === 'mac' ? 'Apple Calendar' : 'Otro calendario (.ics)';
  }

  /**
   * El .ics es un enlace y el navegador tiene que seguirlo. Si el menú se
   * cierra en el propio clic, Angular quita el enlace de la página antes de
   * que el navegador navegue y el clic se pierde: "Calendario del iPhone" no
   * hacía nada. Se cierra justo después.
   */
  cerrarCalendarioTrasNavegar() {
    setTimeout(() => this.showCalendar = false);
  }

  icsHref(row: TicketRow): string | null {
    return icsUrl(this.eventoDeCalendario(row));
  }

  /** Google Calendar con el evento ya relleno; en Android, en la app si se puede. */
  addToGoogleCalendar(row: TicketRow) {
    openGoogleCalendar(this.eventoDeCalendario(row));
    this.showCalendar = false;
  }

  /** Abre Outlook con el evento ya relleno. */
  addToOutlookCalendar(row: TicketRow) {
    const url = outlookCalendarUrl(this.eventoDeCalendario(row));
    if (url) {
      window.open(url, '_blank');
    }
    this.showCalendar = false;
  }

  /**
   * El sitio en Google Maps, o null si no hay dirección: solo con el nombre
   * del espacio la búsqueda puede caer en otra ciudad.
   */
  mapsUrl(row: TicketRow): string | null {
    if (!row.address) {
      return null;
    }
    const destino = [row.spaceName, row.address].filter(Boolean).join(', ');
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destino)}`;
  }

  /** Las entradas del pedido, en PDF. Las genera el TPV, no la web. */
  downloadTickets(row: TicketRow) {
    if (row.refunded || row.isSeasonPassOrder) {
      return;
    }

    this.reportService.getUrlsDownload(row.orderId).subscribe({
      next: (response) => {
        const url = response?.data?.ticket_a4;
        if (url) {
          this.downloadFromUrl(url);
        } else {
          this.openInfoCard('No se pudo obtener la URL de descarga del ticket.', true, false);
        }
      },
      error: () => {
        this.openInfoCard('Lo sentimos, no se han podido descargar las entradas.', true, false);
      }
    });
  }

  private downloadFromUrl(url: string) {
    const newWindow = window.open(url, '_blank');
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      window.location.href = url;
    }
  }

  openInfoCard(msg: string, disabledClose: boolean = false, icon: boolean = true) {
    this.dialog.open(InfoCardComponent, {
      panelClass: ['info-dialog'],
      disableClose: disabledClose,
      data: {
        icon: icon,
        text: msg,
        btnCancel: false
      }
    });
  }

  // ===== Scroll infinito =====

  @HostListener('window:scroll', ['$event'])
  @HostListener('scroll', ['$event'])
  public isScrolled(event: any) {
    if (this.finishScroll || this.loadingPage || !this.containerEvents) {
      return;
    }

    const element = this.containerEvents.nativeElement.getBoundingClientRect();
    if (element.bottom <= window.innerHeight) {
      this.page++;
      this.loadingPage = true;
      this.getData();
    }
  }

  trackByKey(index: number, row: TicketRow) {
    return row.key;
  }
}
